import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import React, { createRef, Fragment } from 'react';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { CountryDropdown } from 'components/layout/form-fields/inputs/CountryDropdown';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { getJobDisplayTitle } from '../openJobsData';
import {
	EXTERNAL_APPLICATION_ENGLISH_LEVEL_OPTIONS,
	EXTERNAL_APPLICATION_REFERRED_SOURCE,
	EXTERNAL_APPLICATION_SOURCE_OPTIONS,
	getExternalApplicationSourceLabel
} from '../externalApplicationOptions';
import { markJobAsApplied } from '../externalApplicationStorage';
import { clearReferralToken } from '../referralStorage';
import ExternalApplicationSuccess from './ExternalApplicationSuccess';

const PDF_MIME = 'application/pdf';
const DOCX_MIME =
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const ACCEPTED_DOCX_TYPES = [DOCX_MIME, 'application/zip', ''];
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const LINKEDIN_PROFILE_REGEX = /linkedin\.com\/in\/[^/?#\s]+/i;

const ValidationSchema = Yup.object().shape({
	name: Yup.string().trim().required().label('Name'),
	email: Yup.string().trim().email().required().label('Email'),
	linkedinUrl: Yup.string()
		.trim()
		.required()
		.test('linkedin-profile', 'Enter a valid LinkedIn profile URL', value =>
			LINKEDIN_PROFILE_REGEX.test(value || '')
		)
		.label('Linkedin'),
	source: Yup.string()
		.when('referralToken', {
			is: token => Boolean(token),
			then: schema => schema.nullable(),
			otherwise: schema => schema.required()
		})
		.label('How did you find us'),
	englishLevel: Yup.string().required().label('English level'),
	countryId: Yup.string().nullable().label('Country'),
	resume: Yup.mixed()
		.required()
		.test('fileFormat', 'Only PDF or DOCX files are allowed', value => {
			if (!value) return false;
			const name = String(value.name || '').toLowerCase();
			const type = value.type || '';
			const isPdf = type === PDF_MIME && name.endsWith('.pdf');
			const isDocx =
				name.endsWith('.docx') && ACCEPTED_DOCX_TYPES.includes(type);
			return isPdf || isDocx;
		})
		.test('fileSize', 'File size must be less than 5MB', value => {
			if (!value) return false;
			return value.size <= MAX_FILE_SIZE;
		})
		.label('Resume')
});

export default class ExternalApplicationCreate extends ModalComponent {
	static isV2 = true;

	constructor(props) {
		super(props);
		this.state = {
			...this.state,
			isDragging: false
		};
		this.fileInputRef = createRef();
	}

	getInitialValues() {
		const { referral } = this.props;

		return {
			name: referral?.name || '',
			email: referral?.email || '',
			linkedinUrl: '',
			source: referral ? EXTERNAL_APPLICATION_REFERRED_SOURCE : '',
			englishLevel: '',
			country: null,
			countryId: '',
			resume: null,
			referralToken: referral?.token || ''
		};
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				validateOnBlur={false}
				validateOnChange={false}
				onSubmit={(values, formikHelpers) => {
					this.saving();
					this.create(values, formikHelpers);
				}}>
				{formikBag =>
					this.renderModalContainer(formikBag, 'ExternalApplicationCreate')
				}
			</Formik>
		);
	}

	renderModalContainer(formikBag, className = '') {
		return (
			<div className={`ExternalApplicationModal ${className}`}>
				<div className="external-application-modal-top">
					<button
						type="button"
						className="external-application-modal-close"
						onClick={() => this.handleClickCancel()}
						aria-label="Close">
						<img src="/ats/icons/close-3-icon.svg" alt="" />
					</button>
				</div>
				<Form>{this.renderForm(formikBag)}</Form>
			</div>
		);
	}

	renderForm(formikBag) {
		const jobTitle = getJobDisplayTitle(this.props.job);
		const isReferral = Boolean(this.props.referral);
		const { isDragging } = this.state;
		const file = formikBag.values.resume;

		return (
			<Fragment>
				<div className="external-application-modal-title">
					<h2>Apply to {jobTitle || 'this role'}</h2>
					<p>Tell us a bit about yourself and share your details to apply.</p>
				</div>
				<div className="external-application-modal-fields">
					{this.renderTextField(formikBag, {
						name: 'name',
						label: 'Name',
						required: true,
						readOnly: isReferral
					})}
					{this.renderTextField(formikBag, {
						name: 'email',
						label: 'Email',
						type: 'email',
						required: true
					})}
					{this.renderTextField(formikBag, {
						name: 'linkedinUrl',
						label: 'Linkedin',
						required: true
					})}
					{isReferral
						? this.renderReadOnlyField({
								label: 'How did you find us?',
								value: getExternalApplicationSourceLabel(
									EXTERNAL_APPLICATION_REFERRED_SOURCE
								)
							})
						: this.renderDropdownField(formikBag, {
								name: 'source',
								label: 'How did you find us?',
								options: EXTERNAL_APPLICATION_SOURCE_OPTIONS,
								required: true
							})}
					{this.renderDropdownField(formikBag, {
						name: 'englishLevel',
						label: 'What is your English level?',
						options: EXTERNAL_APPLICATION_ENGLISH_LEVEL_OPTIONS,
						required: true
					})}
					{this.renderCountryField(formikBag)}
					<div className="external-application-field">
						<div className="external-application-label">
							<span>Resume</span>
							<span className="is-required">*</span>
						</div>
						<div
							className={`external-application-dropzone ${
								isDragging ? 'is-dragging' : ''
							} ${file ? 'has-file' : ''}`}
							onDragEnter={this.handleDragEnter}
							onDragLeave={this.handleDragLeave}
							onDragOver={this.handleDragOver}
							onDrop={event => this.handleDrop(event, formikBag)}>
							<input
								ref={this.fileInputRef}
								type="file"
								accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								onChange={event => this.handleFileInputChange(event, formikBag)}
								className="external-application-file-input"
							/>
							<img
								src="/ats/icons/external-application-resume-icon.svg"
								alt=""
								className="external-application-dropzone-icon"
							/>
							{file ? (
								<div className="external-application-file-info">
									<span>{file.name}</span>
									<button
										type="button"
										className="external-application-remove-file"
										onClick={() => this.handleRemoveFile(formikBag)}>
										<img src="/ats/icons/close-3-icon.svg" alt="Remove" />
									</button>
								</div>
							) : (
								<>
									<p>Drag and drop files here to upload</p>
									<button
										type="button"
										className="external-application-select-files"
										onClick={this.handleSelectFilesClick}>
										Select Files
									</button>
								</>
							)}
						</div>
						{formikBag.errors.resume ? (
							<p className="external-application-error">
								{formikBag.errors.resume}
							</p>
						) : null}
					</div>
				</div>
				<div className="external-application-modal-footer">
					<button
						type="button"
						className="external-application-btn is-secondary"
						onClick={() => this.handleClickCancel()}>
						Cancel
					</button>
					<button
						type="submit"
						className="external-application-btn is-primary"
						disabled={this.state.saving}>
						{this.state.saving ? 'Applying...' : 'Apply'}
					</button>
				</div>
			</Fragment>
		);
	}

	renderReadOnlyField({ label, value }) {
		return (
			<div className="external-application-field">
				<div className="external-application-label">
					<span>{label}</span>
					<span className="is-required">*</span>
				</div>
				<input
					type="text"
					value={value}
					readOnly
					className="external-application-input is-read-only"
				/>
			</div>
		);
	}

	renderTextField(
		formikBag,
		{ name, label, type = 'text', required, readOnly }
	) {
		const hasError = Boolean(formikBag.errors[name]);

		return (
			<div
				className={`external-application-field ${hasError ? 'is-error' : ''}`}>
				<div className="external-application-label">
					<span>{label}</span>
					{required ? <span className="is-required">*</span> : null}
				</div>
				<input
					type={type}
					name={name}
					readOnly={readOnly}
					value={formikBag.values[name]}
					onChange={event => {
						formikBag.handleChange(event);
						if (formikBag.errors[name]) {
							formikBag.setFieldError(name, undefined);
						}
					}}
					className={`external-application-input${readOnly ? ' is-read-only' : ''}`}
				/>
				{hasError ? (
					<p className="external-application-error">{formikBag.errors[name]}</p>
				) : null}
			</div>
		);
	}

	renderDropdownField(formikBag, { name, label, options, required }) {
		const hasError = Boolean(formikBag.errors[name]);
		const value = formikBag.values[name];

		return (
			<div
				className={`external-application-field ${hasError ? 'is-error' : ''} ${
					value ? '' : 'is-placeholder'
				}`}>
				<div className="external-application-label">
					<span>{label}</span>
					{required ? <span className="is-required">*</span> : null}
				</div>
				<Dropdown
					value={value || null}
					placeholder="Select an option"
					options={options}
					optionValue="value"
					onChange={item => {
						formikBag.setFieldValue(name, item);
						formikBag.setFieldError(name, undefined);
					}}
				/>
				{hasError ? (
					<p className="external-application-error">{formikBag.errors[name]}</p>
				) : null}
			</div>
		);
	}

	renderCountryField(formikBag) {
		const hasError = Boolean(formikBag.errors.countryId);

		return (
			<div
				className={`external-application-field ${hasError ? 'is-error' : ''} ${
					formikBag.values.countryId ? '' : 'is-placeholder'
				}`}>
				<div className="external-application-label">
					<span>Country</span>
				</div>
				<CountryDropdown
					public
					value={formikBag.values.country}
					placeholder="Select a country"
					styleVersion="ats"
					onValueChange={value => {
						formikBag.setFieldValue('country', value);
						formikBag.setFieldValue('countryId', value?.id || '');
						formikBag.setFieldError('countryId', undefined);
					}}
				/>
				{hasError ? (
					<p className="external-application-error">
						{formikBag.errors.countryId}
					</p>
				) : null}
			</div>
		);
	}

	handleDragEnter = event => {
		event.preventDefault();
		event.stopPropagation();
		this.setState({ isDragging: true });
	};

	handleDragLeave = event => {
		event.preventDefault();
		event.stopPropagation();
		this.setState({ isDragging: false });
	};

	handleDragOver = event => {
		event.preventDefault();
		event.stopPropagation();
	};

	handleDrop = (event, formikBag) => {
		event.preventDefault();
		event.stopPropagation();
		this.setState({ isDragging: false });
		const droppedFile = event.dataTransfer.files[0];
		if (droppedFile) {
			formikBag.setFieldValue('resume', droppedFile);
			formikBag.setFieldError('resume', undefined);
		}
	};

	handleFileInputChange = (event, formikBag) => {
		const selectedFile = event.target.files[0];
		if (selectedFile) {
			formikBag.setFieldValue('resume', selectedFile);
			formikBag.setFieldError('resume', undefined);
		}
	};

	handleSelectFilesClick = () => {
		this.fileInputRef.current?.click();
	};

	handleRemoveFile = formikBag => {
		formikBag.setFieldValue('resume', null);
		if (this.fileInputRef.current) {
			this.fileInputRef.current.value = '';
		}
	};

	async create(values, formikHelpers) {
		let submitted = false;

		try {
			const result = await jobPositionStore.createPublicExternalApplication(
				this.props.job.id,
				values
			);

			if (result.status === 'success') {
				submitted = true;
				markJobAsApplied(this.props.job.id);
				clearReferralToken(this.props.job.id);
				this.props.handleSuccess?.(result);
				modalStore.openModal(
					{ isV2: true, position: 'center' },
					<ExternalApplicationSuccess />
				);
				return;
			}

			if (result.code === 'email_already_applied') {
				formikHelpers.setFieldError(
					'email',
					result.message || 'This email already applied to this position.'
				);
				return;
			}

			if (result.code === 'invalid_linkedin') {
				formikHelpers.setFieldError(
					'linkedinUrl',
					result.message || 'Enter a valid LinkedIn profile URL'
				);
				return;
			}

			if (result.code === 'invalid_referral') {
				clearReferralToken(this.props.job.id);
				this.props.onReferralInvalid?.();
				modalStore.closeDialog();
				notificationStore.pushErrorNotification(
					'Confirm application',
					'This referral link is no longer valid. You can still apply to this position.'
				);
				return;
			}

			notificationStore.pushErrorNotification(
				'Apply',
				result.message || 'An error has occurred. Please try again.'
			);
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Apply',
				error?.message || 'An error has occurred. Please try again.'
			);
		} finally {
			if (!submitted) {
				this.saving(false);
			}
		}
	}
}
